import { createAction } from "@reduxjs/toolkit";

import { ISearchRequest } from "../../components/application/search/SearchBar";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";
import { IChemicalSearchModelResult } from "../../data/ChemicalSearchModelResult";
import type {
    AnalyzeMultiStepReactionRequest,
    AnalyzeMultiStepReactionResponse,
    ConvertMultiStepReactionRequest,
    ConvertMultiStepReactionResponse,
    ConvertReactionRequest,
    ConvertReactionResponse,
    PredictedComponent,
    PredictionApiOptions,
    PredictionRequest,
    WebSocketReactionAnalyzeRealtimePayload,
    WebSocketReactionAnalyzeRealtimeSuccess,
    WebSocketReactionAnalyzeRealtimeUpdate,
    WebSocketReactionRouteAnalyzeRealtimePayload,
    WebSocketReactionRouteAnalyzeRealtimeSuccess,
    WebSocketReactionRouteAnalyzeRealtimeUpdate,
    ReactionGroup,
    SimplePredictionResponse,
    SimpleReactionRequest,
    ValidationResult,
} from "../../data/models/schemas";
import type { BigDecimal } from "../../utils/bigDecimal";
import { IReactionChemicalType, IReactionChemical } from "./library";
import {
    IReaction,
    IReactionPropertyRequest,
    IReactionPropertyResponse,
    IMultiStepReaction,
} from "../reducers/reactions";
import { createSocketAction } from "../middlewares/websocket";
import { refreshLicenseInfoMeta } from "../utils/meteringMeta";

export const REACTIONS_SHOW_EDIT_MODAL = "REACTIONS_SHOW_EDIT_MODAL";
export const REACTIONS_SUBMIT_EDIT_MODAL = "REACTIONS_SUBMIT_EDIT_MODAL";
export const REACTIONS_HIDE_EDIT_MODAL = "REACTIONS_HIDE_EDIT_MODAL";

export const REACTIONS_SHOW_LOAD_MODAL = "REACTIONS_SHOW_LOAD_MODAL";
export const REACTIONS_SUBMIT_LOAD_MODAL = "REACTIONS_SUBMIT_LOAD_MODAL";
export const REACTIONS_HIDE_LOAD_MODAL = "REACTIONS_HIDE_LOAD_MODAL";

export const REACTIONS_SHOW_CHEMICAL_EDIT_MODAL =
    "REACTIONS_SHOW_CHEMICAL_EDIT_MODAL";
export const REACTIONS_SUBMIT_CHEMICAL_EDIT_MODAL =
    "REACTIONS_SUBMIT_CHEMICAL_EDIT_MODAL";
export const REACTIONS_HIDE_CHEMICAL_EDIT_MODAL =
    "REACTIONS_HIDE_CHEMICAL_EDIT_MODAL";
export const REACTIONS_REMOVE_REACTION = "REACTIONS_REMOVE_REACTION";
export const MULTISTEP_REACTIONS_SUBMIT_EDIT_MODAL =
    "MULTISTEP_REACTIONS_SUBMIT_EDIT_MODAL";
export const MULTISTEP_REACTIONS_REMOVE_REACTION =
    "MULTISTEP_REACTIONS_REMOVE_REACTION";
export const REACTIONS_SHOW_UPLOAD_MODAL = "REACTIONS_SHOW_UPLOAD_MODAL";
export const REACTIONS_HIDE_UPLOAD_MODAL = "REACTIONS_HIDE_UPLOAD_MODAL";

export const REACTIONS_SHOW_IMPORT_MODAL = "REACTIONS_SHOW_IMPORT_MODAL";
export const REACTIONS_HIDE_IMPORT_MODAL = "REACTIONS_HIDE_IMPORT_MODAL";

export const REACTIONS_ANALYZE = "REACTIONS_ANALYZE";
export const REACTION_ANALYZE_REALTIME = "REACTION_ANALYZE_REALTIME";
export const REACTIONS_CONVERT = "REACTIONS_CONVERT";
export const MULTISTEP_REACTIONS_ANALYZE = "MULTISTEP_REACTIONS_ANALYZE";
export const REACTION_ROUTE_ANALYZE_REALTIME =
    "REACTION_ROUTE_ANALYZE_REALTIME";
export const MULTISTEP_REACTIONS_CONVERT = "MULTISTEP_REACTIONS_CONVERT";

export const REACTIONS_PREDICT_REACTANTS = "REACTIONS_PREDICT_REACTANTS";
export const REACTIONS_PREDICT_REAGENTS = "REACTIONS_PREDICT_REAGENTS";
export const REACTIONS_PREDICT_PRODUCTS = "REACTIONS_PREDICT_PRODUCTS";
export const REACTIONS_PREDICT_COMPONENTS = "REACTIONS_PREDICT_COMPONENTS";
export const REACTIONS_PREDICT_CATALYST = "REACTIONS_PREDICT_CATALYST";
export const REACTIONS_PREDICT_SOLVENT = "REACTIONS_PREDICT_SOLVENT";

export const REACTIONS_SHOW_PREDICTION_MODAL =
    "REACTIONS_SHOW_PREDICTION_MODAL";
export const REACTIONS_HIDE_PREDICTION_MODAL =
    "REACTIONS_HIDE_PREDICTION_MODAL";
export const REACTIONS_SET_PREDICTION_CANDIDATES =
    "REACTIONS_SET_PREDICTION_CANDIDATES";
export const REACTIONS_SELECT_PREDICTION_CANDIDATE =
    "REACTIONS_SELECT_PREDICTION_CANDIDATE";
export const REACTIONS_UNSELECT_PREDICTION_CANDIDATE =
    "REACTIONS_UNSELECT_PREDICTION_CANDIDATE";
export const REACTIONS_ADD_PREDICTION_CANDIDATES =
    "REACTIONS_ADD_PREDICTION_CANDIDATES";

export const REACTIONS_SET_ANALYZER_MODE = "REACTIONS_SET_ANALYZER_MODE";
export const REACTIONS_SET_SHOWN_MULTI_STEP = "REACTIONS_SET_SHOWN_MULTI_STEP";
export const REACTIONS_SET_CURRENT_MULTI_STEP =
    "REACTIONS_SET_CURRENT_MULTI_STEP";

export const MULTISTEP_SHOW_STEP_MODAL = "MULTISTEP_SHOW_STEP_MODAL";
export const MULTISTEP_HIDE_STEP_MODAL = "MULTISTEP_HIDE_STEP_MODAL";
export const MULTISTEP_SET_ACTIVE_STEP = "MULTISTEP_SET_ACTIVE_STEP";

export const REACTION_SET_NAME = "REACTION_SET_NAME";
export const REACTION_SET_DESCRIPTION = "REACTION_SET_DESCRIPTION";
export const REACTION_SET_SHOWN_REACTION = "REACTION_SET_SHOWN_REACTION";
export const REACTION_SET_SHOWN_NAME = "REACTION_SET_SHOWN_NAME";
export const REACTION_REMOVE_REACTION_CHEMICAL =
    "REACTION_REMOVE_REACTION_CHEMICAL";

export const REACTION_CHEMICAL_SET_NAME = "REACTION_CHEMICAL_SET_NAME";
export const REACTION_CHEMICAL_SET_DESCRIPTION =
    "REACTION_CHEMICAL_SET_DESCRIPTION";
export const REACTION_CHEMICAL_SET_CHEMICAL_TYPE =
    "REACTION_CHEMICAL_SET_CHEMICAL_TYPE";
export const REACTION_CHEMICAL_SET_MASS_VALUE =
    "REACTION_CHEMICAL_SET_MASS_VALUE";
export const REACTION_CHEMICAL_SET_MASS_UNIT =
    "REACTION_CHEMICAL_SET_MASS_UNIT";
export const REACTION_CHEMICAL_SET_AMOUNT_VALUE =
    "REACTION_CHEMICAL_SET_AMOUNT_VALUE";
export const REACTION_CHEMICAL_SET_AMOUNT_UNIT =
    "REACTION_CHEMICAL_SET_AMOUNT_UNIT";

export const REACTION_CHEMICAL_SEARCH = "REACTION_CHEMICAL_SEARCH";

export const REACTION_CHEMICAL_REMOVE_LINKED_CHEMICAL =
    "REACTION_CHEMICAL_REMOVE_LINKED_CHEMICAL";
export const REACTION_CHEMICAL_SUBMIT_LINKED_REACTION =
    "REACTION_CHEMICAL_SUBMIT_LINKED_REACTION";
export const REACTION_CHEMICAL_REMOVE_LINKED_REACTION =
    "REACTION_CHEMICAL_REMOVE_LINKED_REACTION";

export const REACTION_CHEMICAL_SHOW_REACTION_LINK_MODAL =
    "REACTION_CHEMICAL_SHOW_REACTION_LINK_MODAL";
export const REACTION_CHEMICAL_HIDE_REACTION_LINK_MODAL =
    "REACTION_CHEMICAL_HIDE_REACTION_LINK_MODAL";

export const REACTION_CHEMICAL_GET_CHEMICAL_TYPES =
    "REACTION_CHEMICAL_GET_CHEMICAL_TYPES";

export const showReactionsEditModal = createAction<IReaction>(
    REACTIONS_SHOW_EDIT_MODAL
);

export const submitReactionsEditModal = createAction<IReaction>(
    REACTIONS_SUBMIT_EDIT_MODAL
);

export const hideReactionsEditModal = createAction(REACTIONS_HIDE_EDIT_MODAL);

export const showReactionsLoadModal = createAction(REACTIONS_SHOW_LOAD_MODAL);
export const submitReactionsLoadModal = createAction<IReaction>(
    REACTIONS_SUBMIT_LOAD_MODAL
);
export const hideReactionsLoadModal = createAction(REACTIONS_HIDE_LOAD_MODAL);

export const removeReaction = createAction<IReaction>(
    REACTIONS_REMOVE_REACTION
);

export const submitMultiStepReactionsEditModal =
    createAction<IMultiStepReaction>(MULTISTEP_REACTIONS_SUBMIT_EDIT_MODAL);

export const removeMultiStepReaction = createAction<IMultiStepReaction>(
    MULTISTEP_REACTIONS_REMOVE_REACTION
);

export const showReactionChemicalEditModal = createAction<
    IReactionChemical | undefined
>(REACTIONS_SHOW_CHEMICAL_EDIT_MODAL);

export const submitReactionChemicalEditModal = createAction<IReactionChemical>(
    REACTIONS_SUBMIT_CHEMICAL_EDIT_MODAL
);

export const hideReactionChemicalEditModal = createAction(
    REACTIONS_HIDE_CHEMICAL_EDIT_MODAL
);

export const showReactionChemicalReactionLinkModal = createAction(
    REACTION_CHEMICAL_SHOW_REACTION_LINK_MODAL
);

export const hideReactionChemicalReactionLinkModal = createAction(
    REACTION_CHEMICAL_HIDE_REACTION_LINK_MODAL
);

export const showUploadModal = createAction(REACTIONS_SHOW_UPLOAD_MODAL);

export const hideUploadModal = createAction(REACTIONS_HIDE_UPLOAD_MODAL);

export const showImportModal = createAction(REACTIONS_SHOW_IMPORT_MODAL);

export const hideImportModal = createAction(REACTIONS_HIDE_IMPORT_MODAL);

export const setAnalyzerMode = createAction<"single" | "multi-step">(
    REACTIONS_SET_ANALYZER_MODE
);

export const setShownMultiStepReaction =
    createAction<IMultiStepReaction | null>(REACTIONS_SET_SHOWN_MULTI_STEP);

export const setCurrentMultiStepReaction =
    createAction<IMultiStepReaction | null>(REACTIONS_SET_CURRENT_MULTI_STEP);

export const showMultiStepStepModal = createAction<{
    stepId: string;
    stepIndex: number;
}>(MULTISTEP_SHOW_STEP_MODAL);

export const hideMultiStepStepModal = createAction(MULTISTEP_HIDE_STEP_MODAL);

export const setMultiStepActiveStep = createAction<number>(
    MULTISTEP_SET_ACTIVE_STEP
);

export const analyzeReaction = createRsaaAction<
    IReactionPropertyRequest,
    IReactionPropertyResponse
>({
    path: "reactions/analyze",
    method: "POST",
    type: REACTIONS_ANALYZE,
    meta: () => refreshLicenseInfoMeta,
});

export type IReactionAnalysisRealtimeSocketPayload =
    WebSocketReactionAnalyzeRealtimePayload;
export type IReactionAnalysisRealtimeSocketResponse =
    | WebSocketReactionAnalyzeRealtimeUpdate
    | WebSocketReactionAnalyzeRealtimeSuccess;
export type IReactionAnalysisRealtimeSocketError = {
    message?: string;
    code?: string;
};

export const analyzeReactionRealtime = createSocketAction<
    IReactionAnalysisRealtimeSocketPayload,
    IReactionAnalysisRealtimeSocketResponse,
    IReactionAnalysisRealtimeSocketError
>({
    type: REACTION_ANALYZE_REALTIME,
    meta: refreshLicenseInfoMeta,
});

export const analyzeMultiStepReaction = createRsaaAction<
    IMultiStepAnalysisRequest,
    IMultiStepAnalysisResponse
>({
    path: "reactions/analyze/multi",
    method: "POST",
    type: MULTISTEP_REACTIONS_ANALYZE,
    meta: () => refreshLicenseInfoMeta,
});

export type IRouteAnalysisRealtimeSocketPayload =
    WebSocketReactionRouteAnalyzeRealtimePayload;
export type IRouteAnalysisRealtimeSocketResponse =
    | WebSocketReactionRouteAnalyzeRealtimeUpdate
    | WebSocketReactionRouteAnalyzeRealtimeSuccess;
export type IRouteAnalysisRealtimeSocketError = {
    message?: string;
    code?: string;
};

export const analyzeMultiStepReactionRealtime = createSocketAction<
    IRouteAnalysisRealtimeSocketPayload,
    IRouteAnalysisRealtimeSocketResponse,
    IRouteAnalysisRealtimeSocketError
>({
    type: REACTION_ROUTE_ANALYZE_REALTIME,
    meta: refreshLicenseInfoMeta,
});

export type IConvertReactionRequest = ConvertReactionRequest;
export type IConvertReactionResponse = ConvertReactionResponse;
export type IConvertMultiStepReactionRequest = ConvertMultiStepReactionRequest;
export type IConvertMultiStepReactionResponse =
    ConvertMultiStepReactionResponse;
export type IMultiStepAnalysisRequest = AnalyzeMultiStepReactionRequest;
export type IMultiStepAnalysisResponse = AnalyzeMultiStepReactionResponse;

export const convertReaction = createRsaaAction<
    IConvertReactionRequest,
    IConvertReactionResponse
>({
    path: "reactions/convert",
    method: "POST",
    type: REACTIONS_CONVERT,
});

export const convertMultiStepReaction = createRsaaAction<
    IConvertMultiStepReactionRequest,
    IConvertMultiStepReactionResponse
>({
    path: "reactions/convert-multistep",
    method: "POST",
    type: MULTISTEP_REACTIONS_CONVERT,
    meta: () => ({}),
});

export const setReactionName = createAction<string>(REACTION_SET_NAME);
export const setReactionDescription = createAction<string>(
    REACTION_SET_DESCRIPTION
);
export const setShownReaction = createAction<IReaction>(
    REACTION_SET_SHOWN_REACTION
);
export const setShownReactionName = createAction<string>(
    REACTION_SET_SHOWN_NAME
);
export const removeReactionChemical = createAction<IReactionChemical>(
    REACTION_REMOVE_REACTION_CHEMICAL
);

export const setReactionChemicalName = createAction<string>(
    REACTION_CHEMICAL_SET_NAME
);
export const setReactionChemicalDescription = createAction<string>(
    REACTION_CHEMICAL_SET_DESCRIPTION
);
export const setReactionChemicalType = createAction<IReactionChemicalType>(
    REACTION_CHEMICAL_SET_CHEMICAL_TYPE
);
export const setReactionChemicalMassValue = createAction<
    BigDecimal | undefined
>(REACTION_CHEMICAL_SET_MASS_VALUE);
export const setReactionChemicalMassUnit = createAction<string>(
    REACTION_CHEMICAL_SET_MASS_UNIT
);
export const setReactionChemicalAmountValue = createAction<{
    unit: string;
    value: BigDecimal | undefined;
}>(REACTION_CHEMICAL_SET_AMOUNT_VALUE);
export const setReactionChemicalAmountUnit = createAction<string>(
    REACTION_CHEMICAL_SET_AMOUNT_UNIT
);

export const searchReactionChemical = createRsaaAction<
    ISearchRequest,
    IChemicalSearchModelResult
>({
    path: "chemicals/search",
    method: "POST",
    type: REACTION_CHEMICAL_SEARCH,
    meta: () => refreshLicenseInfoMeta,
});

export const getReactionChemicalTypes = createRsaaAction<
    void,
    IReactionChemicalType[]
>({
    path: "reactions/chemical_types",
    method: "GET",
    type: REACTION_CHEMICAL_GET_CHEMICAL_TYPES,
});

export const removeReactionChemicalLinkedChemical = createAction(
    REACTION_CHEMICAL_REMOVE_LINKED_CHEMICAL
);

export const submitReactionChemicalLinkedReaction = createAction<string>(
    REACTION_CHEMICAL_SUBMIT_LINKED_REACTION
);

export const removeReactionChemicalLinkedReaction = createAction(
    REACTION_CHEMICAL_REMOVE_LINKED_REACTION
);

// Prediction-related interfaces
// TODO(orval-drift): synthesisContext is not in the OpenAPI schema yet.
export type IPredictionRequest = PredictionRequest & {
    synthesisContext?: ISynthesisContext;
};
export type ISimpleReactionRequest = SimpleReactionRequest;
export type IPredictionApiOptions = PredictionApiOptions;

export interface ISynthesisContext {
    temperatureRange?: string;
    targetMolecule?: string;
    maxTemperature?: number;
    minTemperature?: number;
    detectedPreferences?: string[];
    avoidedSolvents?: string[];
    successfulCatalysts?: string[];
    overallObjective?: string;
    currentStepCount?: number;
    solventUsage?: string;
    activeProtectingGroups?: string[];
    requiredPurity?: number;
    temperatureEscalationStrategy?: string;
    preferredSolvents?: string[];
    solventCompatibilityRequired?: boolean;
    avoidedFunctionalGroups?: string[];
    synthesisStrategy?: string;
    strategy?: string;
    stepCount?: number;
}

export interface IPredictionCandidate {
    id: string;
    smiles: string;
    name: string;
    confidence: number;
    role: string; // "reactant", "reagent", "product", "catalyst", "solvent"
    rationale: string;
    safetyAnalysis?: {
        hazardScore: number;
        ghsCodes: string[];
        handlingRecommendations: string[];
    };
    availability?: {
        commercial: boolean;
        cost: string;
        suppliers: string[];
    };
    compatibility?: {
        score: number;
        issues: string[];
    };
}

// Component and group interfaces for new unified response
export type IPredictedComponent = PredictedComponent;
export type IReactionGroup = ReactionGroup;
export type IValidationResult = ValidationResult;
type PredictionList = IPredictedComponent[];
type PredictionMetadata = Record<string, unknown>;

// New unified SimplePredictionResponse interface based on OpenAPI spec
export type ISimplePredictionResponse = SimplePredictionResponse;

// Legacy interfaces kept for backward compatibility but mapped to SimplePredictionResponse
export interface IReactantResponse extends ISimplePredictionResponse {
    alternativeStartingMaterials?: PredictionList;
    reactantPredictions?: PredictionList;
    primaryReactant?: IPredictedComponent;
    functionalGroupCompatibility?: string;
    syntheticAccessibility?: string;
    commercialAvailability?: string;
    retrosynthesisAnalysis?: string;
    costAnalysis?: string;
    disconnectionAnalysis?: string;
}

export interface IReagentResponse extends ISimplePredictionResponse {
    reagentPredictions?: PredictionList;
    primaryReagent?: IPredictedComponent;
    alternativeReagents?: PredictionList;
    reactivityAnalysis?: string;
    stoichiometricAnalysis?: string;
    safetyAnalysis?: string;
    compatibilityAnalysis?: string;
    conditionRequirements?: string;
    reagentSystem?: string;
}

export interface IProductResponse extends ISimplePredictionResponse {
    productPredictions?: PredictionList;
    majorProduct?: IPredictedComponent;
    minorProducts?: PredictionList;
    sideProducts?: PredictionList;
    yieldPrediction?: string;
    regiochemicalAnalysis?: string;
    stereochemicalAnalysis?: string;
    selectivityAnalysis?: string;
    mechanismPathway?: string;
}

export interface IGroupedPredictionResponse extends ISimplePredictionResponse {
    reactionGroups?: IReactionGroup[];
    metadata?: PredictionMetadata;
    overallConfidence?: unknown;
}

export interface ICatalystResponse extends ISimplePredictionResponse {
    catalystPredictions?: PredictionList;
    bestCatalyst?: IPredictedComponent;
    mechanismAnalysis?: string;
    substrateScope?: string;
    conditionRequirements?: string;
    compatibilityAssessment?: string;
}

export interface ISolventResponse extends ISimplePredictionResponse {
    solventPredictions?: PredictionList;
    primarySolvent?: IPredictedComponent;
    alternativeSolvents?: PredictionList;
    greenAlternatives?: PredictionList;
    polarityAnalysis?: string;
    solubilityAnalysis?: string;
    environmentalImpact?: string;
    safetyConsiderations?: string;
}

export type IPredictionResponse = ISimplePredictionResponse;

export type PredictionType =
    | "reactants"
    | "reagents"
    | "products"
    | "components"
    | "catalyst"
    | "solvent";

export interface IPredictionModalState {
    show: boolean;
    type: PredictionType | null;
    loading: boolean;
    error: string | null;
    candidates: IPredictionCandidate[];
    selectedCandidates: string[]; // Array of candidate IDs
}

// Prediction action creators - all now use SimplePredictionResponse
export const predictReactants = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/reactants",
    method: "POST",
    type: REACTIONS_PREDICT_REACTANTS,
    meta: () => refreshLicenseInfoMeta,
});

export const predictReagents = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/reagents",
    method: "POST",
    type: REACTIONS_PREDICT_REAGENTS,
    meta: () => refreshLicenseInfoMeta,
});

export const predictProducts = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/products",
    method: "POST",
    type: REACTIONS_PREDICT_PRODUCTS,
    meta: () => refreshLicenseInfoMeta,
});

export const predictComponents = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/components",
    method: "POST",
    type: REACTIONS_PREDICT_COMPONENTS,
    meta: () => refreshLicenseInfoMeta,
});

export const predictCatalyst = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/catalyst",
    method: "POST",
    type: REACTIONS_PREDICT_CATALYST,
    meta: () => refreshLicenseInfoMeta,
});

export const predictSolvent = createRsaaAction<
    IPredictionRequest,
    ISimplePredictionResponse
>({
    path: "reactions/predict/solvent",
    method: "POST",
    type: REACTIONS_PREDICT_SOLVENT,
    meta: () => refreshLicenseInfoMeta,
});

// Prediction modal management actions
export const showPredictionModal = createAction<PredictionType>(
    REACTIONS_SHOW_PREDICTION_MODAL
);
export const hidePredictionModal = createAction(
    REACTIONS_HIDE_PREDICTION_MODAL
);
export const setPredictionCandidates = createAction<IPredictionCandidate[]>(
    REACTIONS_SET_PREDICTION_CANDIDATES
);
export const selectPredictionCandidate = createAction<string>(
    REACTIONS_SELECT_PREDICTION_CANDIDATE
);
export const unselectPredictionCandidate = createAction<string>(
    REACTIONS_UNSELECT_PREDICTION_CANDIDATE
);
export const addPredictionCandidates = createAction<IPredictionCandidate[]>(
    REACTIONS_ADD_PREDICTION_CANDIDATES
);
